/**
 * Titan Store
 *
 * Zustand state management for the Titan trading system.
 * All API calls go through the centralized api.ts client — no direct fetch() here.
 */

import { create } from 'zustand';
import { patternsApi, titanApi, ApiError } from '../lib/api';
import type {
  TitanPattern,
  PatternSummary,
  TitanCredentialStatus,
  TitanStatus,
  MarketSnapshot,
  UpdatePatternRequest,
} from '../types/titan';

interface TitanState {
  // Credentials
  credentialStatus: TitanCredentialStatus | null;
  credentialStatusLoading: boolean;
  credentialStatusError: string | null;

  // System status
  systemStatus: TitanStatus | null;
  systemStatusLoading: boolean;
  systemStatusError: string | null;

  // Patterns
  patterns: PatternSummary[];
  patternsLoading: boolean;
  patternsError: string | null;
  /** Last activate/deactivate failure (e.g. live-pattern cap). Cleared on success. */
  patternActionError: string | null;

  // Current pattern (for editing/viewing)
  currentPattern: TitanPattern | null;
  currentPatternLoading: boolean;
  currentPatternError: string | null;

  // Translation
  isTranslating: boolean;
  translationError: string | null;
  lastTranslatedPattern: TitanPattern | null;

  // Market data
  marketSnapshot: MarketSnapshot | null;
  marketSnapshotLoading: boolean;
  marketSnapshotError: string | null;

  // Actions
  fetchCredentialStatus: () => Promise<void>;
  fetchSystemStatus: () => Promise<void>;
  fetchPatterns: () => Promise<void>;
  fetchPattern: (patternId: string) => Promise<void>;
  translatePattern: (description: string, name?: string) => Promise<TitanPattern | null>;
  activatePattern: (patternId: string) => Promise<boolean>;
  deactivatePattern: (patternId: string) => Promise<boolean>;
  deletePattern: (patternId: string) => Promise<boolean>;
  updatePattern: (patternId: string, updates: UpdatePatternRequest) => Promise<TitanPattern | null>;
  setCurrentPattern: (pattern: TitanPattern | null) => void;
  clearCurrentPattern: () => void;
  fetchMarketSnapshot: (exchange?: string) => Promise<void>;
  clearTranslation: () => void;
}

export const useTitanStore = create<TitanState>((set, get) => ({
  // Initial state
  credentialStatus: null,
  credentialStatusLoading: false,
  credentialStatusError: null,
  systemStatus: null,
  systemStatusLoading: false,
  systemStatusError: null,
  patterns: [],
  patternsLoading: false,
  patternsError: null,
  patternActionError: null,
  currentPattern: null,
  currentPatternLoading: false,
  currentPatternError: null,
  isTranslating: false,
  translationError: null,
  lastTranslatedPattern: null,
  marketSnapshot: null,
  marketSnapshotLoading: false,
  marketSnapshotError: null,

  fetchCredentialStatus: async () => {
    set({ credentialStatusLoading: true, credentialStatusError: null });
    try {
      const data = await titanApi.status();
      // credential status shape lives inside the status response
      set({ credentialStatus: (data as any).credentials ?? null, credentialStatusLoading: false });
    } catch (error) {
      const message = error instanceof ApiError ? error.detail : (error instanceof Error ? error.message : 'Unknown error');
      set({ credentialStatusError: message, credentialStatusLoading: false });
    }
  },

  fetchSystemStatus: async () => {
    set({ systemStatusLoading: true, systemStatusError: null });
    try {
      const data = await titanApi.status();
      set({ systemStatus: data as unknown as TitanStatus, systemStatusLoading: false });
    } catch (err) {
      const message = err instanceof ApiError ? err.detail : (err instanceof Error ? err.message : 'Failed to load system status');
      set({ systemStatusError: message, systemStatusLoading: false });
    }
  },

  fetchPatterns: async () => {
    set({ patternsLoading: true, patternsError: null });
    try {
      const data = await patternsApi.list();
      set({ patterns: data.patterns || [], patternsLoading: false });
    } catch (error) {
      const message = error instanceof ApiError ? error.detail : (error instanceof Error ? error.message : 'Unknown error');
      set({ patternsError: message, patternsLoading: false });
    }
  },

  fetchPattern: async (patternId: string) => {
    set({ currentPatternLoading: true, currentPatternError: null });
    try {
      const data = await patternsApi.get(patternId);
      set({ currentPattern: data, currentPatternLoading: false });
    } catch (err) {
      const message = err instanceof ApiError ? err.detail : (err instanceof Error ? err.message : 'Failed to load pattern');
      set({ currentPattern: null, currentPatternError: message, currentPatternLoading: false });
    }
  },

  translatePattern: async (description: string, name?: string) => {
    set({ isTranslating: true, translationError: null, lastTranslatedPattern: null });
    try {
      const pattern = await patternsApi.translate({ description, name });
      set({ isTranslating: false, lastTranslatedPattern: pattern });
      get().fetchPatterns();
      return pattern;
    } catch (error) {
      const message = error instanceof ApiError ? error.detail : (error instanceof Error ? error.message : 'Unknown error');
      set({ translationError: message, isTranslating: false });
      return null;
    }
  },

  activatePattern: async (patternId: string) => {
    // Optimistic update
    const previousPatterns = get().patterns;
    set({
      patternActionError: null,
      patterns: previousPatterns.map(p => 
        p.id === patternId ? { ...p, is_active: true } : p
      )
    });

    try {
      await patternsApi.activate(patternId);
      set({ patternActionError: null });
      get().fetchPatterns();
      return true;
    } catch (error) {
      const message = error instanceof ApiError ? error.detail : (error instanceof Error ? error.message : 'Activation failed');
      set({ patterns: previousPatterns, patternActionError: message });
      return false;
    }
  },

  deactivatePattern: async (patternId: string) => {
    // Optimistic update
    const previousPatterns = get().patterns;
    set({
      patternActionError: null,
      patterns: previousPatterns.map(p => 
        p.id === patternId ? { ...p, is_active: false } : p
      )
    });

    try {
      await patternsApi.deactivate(patternId);
      set({ patternActionError: null });
      get().fetchPatterns();
      return true;
    } catch (error) {
      const message = error instanceof ApiError ? error.detail : (error instanceof Error ? error.message : 'Deactivation failed');
      set({ patterns: previousPatterns, patternActionError: message });
      return false;
    }
  },

  deletePattern: async (patternId: string) => {
    // Optimistic update
    const previousPatterns = get().patterns;
    set({
      patterns: previousPatterns.filter(p => p.id !== patternId)
    });

    try {
      await patternsApi.delete(patternId);
      return true;
    } catch (error) {
      // Revert on failure
      set({ patterns: previousPatterns });
      return false;
    }
  },

  updatePattern: async (patternId: string, updates: UpdatePatternRequest) => {
    try {
      const data = await patternsApi.update(patternId, updates);
      get().fetchPatterns();
      if (get().currentPattern?.id === patternId) {
        set({ currentPattern: data as unknown as TitanPattern });
      }
      return data as unknown as TitanPattern;
    } catch (error) {
      // Re-throw so callers can handle Pydantic validation detail arrays
      throw error;
    }
  },

  setCurrentPattern: (pattern) => set({ currentPattern: pattern }),

  clearCurrentPattern: () => set({ currentPattern: null }),

  fetchMarketSnapshot: async (exchange = 'US') => {
    set({ marketSnapshotLoading: true, marketSnapshotError: null });
    try {
      const data = await titanApi.marketSnapshot(exchange);
      set({ marketSnapshot: data as MarketSnapshot, marketSnapshotLoading: false });
    } catch (err) {
      const message = err instanceof ApiError ? err.detail : (err instanceof Error ? err.message : 'Failed to load market snapshot');
      set({ marketSnapshotError: message, marketSnapshotLoading: false });
    }
  },

  clearTranslation: () => set({
    isTranslating: false,
    translationError: null,
    lastTranslatedPattern: null,
  }),
}));
